/**
 * ═══════════════════════════════════════════════════════════════
 *  MASTER DESIGN SYSTEM — The Domino Game
 * ═══════════════════════════════════════════════════════════════
 *
 * Single source of truth for all visual styling across every scene.
 * All colors, fonts, spacing, radii, strokes, shadows, gradients,
 * surface styles, and button variants live here.
 *
 * Design sources:
 *  - Figma screen 1: dark pill controls, Montserrat 600/20px, gold glow
 *  - Figma screen 2: Nunito 700 titles, pill buttons 32px, #CB9719 CTA
 *  - Project palette: black bg, #FFD700 gold, #0099FF teamA, #FF4949 teamB
 *
 * BACKWARD COMPATIBILITY: All original exports are preserved exactly.
 * New tokens are additions only — nothing was renamed or removed.
 * ═══════════════════════════════════════════════════════════════
 */

// ─────────────────────────────────────────────────────────────────
// RESPONSIVE SCALING
// Returns a scale factor (0.6–1.0) based on screen width.
// Desktop (1280+) = 1.0 · Phone (360) = 0.6
// ─────────────────────────────────────────────────────────────────

export function getUIScale(scene: Phaser.Scene): number {
  const w = scene.scale.width;
  if (w >= 1000) return 1;
  if (w <= 360) return 0.6;
  return 0.6 + (w - 360) / (1000 - 360) * 0.4;
}

/** Returns a responsive font size string like "24px" */
export function responsiveFont(basePx: number, scene: Phaser.Scene): string {
  return `${Math.round(basePx * getUIScale(scene))}px`;
}

/** Returns a responsive pixel value */
export function responsiveSize(basePx: number, scene: Phaser.Scene): number {
  return Math.round(basePx * getUIScale(scene));
}


// ─────────────────────────────────────────────────────────────────
// COLORS
// Hex numbers (0x) for Phaser Graphics · strings ('#') for Text
// ─────────────────────────────────────────────────────────────────

export const COLORS = {

  // ── Backgrounds ──────────────────────────────────────────────
  bgPrimary:    0x000000,   // Pure black app background
  bgSecondary:  0x0d0d0d,   // Slightly lighter black for cards
  bgCard:       0x1a1a1a,   // Dark card background
  bgCardHover:  0x252525,   // Card hover state
  bgOverlay:    0x111111,   // Modal / overlay backgrounds
  bgSurface:    0x1f1f1f,   // Figma dark surface (slightly lighter than card)

  // ── Board (warm wood table feel) ─────────────────────────────
  bgFelt:       0x2a1a0e,   // Dark warm brown
  bgFeltGrid:   0x3a2515,   // Subtle wood grain lines
  bgFeltFrame:  0x5c3a1e,   // Wood frame border
  bgFeltInner:  0x1e120a,   // Inner table shadow

  // ── Gold / Accent ─────────────────────────────────────────────
  gold:              0xffd700,  // Primary gold (brightest)
  goldBright:        0xffd700,  // Alias — kept for backward compat
  goldDark:          0xcb9719,  // Darker gold (Figma CTA button)
  goldMuted:         0x8b7328,  // Muted gold for inactive states
  goldGradientStart: 0xffeecc,  // Figma gold gradient light end
  goldGradientEnd:   0xad9515,  // Figma gold gradient dark end
  goldGlow:          0xffe898,  // Figma gold glow / flare
  whiteFlare:        0xffffff,  // Figma white flare highlight

  // ── Teams ─────────────────────────────────────────────────────
  teamA:      0x0099ff,  // Team A blue  (Figma: #0099FF)
  teamB:      0xff4949,  // Team B red   (Figma: #FF4949)
  teamALight: 0x4dc3ff,  // Light blue highlights
  teamBLight: 0xff7777,  // Light red highlights

  // ── Buttons ───────────────────────────────────────────────────
  btnPrimary:       0xffd700,  // Gold primary CTA
  btnSecondary:     0x2a2a2a,  // Dark secondary
  btnSecondaryLight:0xf5f5f5,  // Light secondary (Figma screen 2)
  btnDanger:        0x8b0000,  // Danger / leave
  btnSuccess:       0x27ae60,  // Success / confirm
  btnOnline:        0x1a5276,  // Online mode tint

  // ── Text (CSS strings for Phaser Text objects) ────────────────
  textWhite:      '#ffffff',
  textGold:       '#ffd700',
  textGoldBright: '#ffd700',
  textGoldDark:   '#cb9719',
  textGoldGlow:   '#ffe898',
  textGray:       '#888888',
  textGrayLight:  '#aaaaaa',
  textGrayDark:   '#555555',
  textGreen:      '#00ff00',
  textRed:        '#ff6666',
  textCyan:       '#4ecdc4',
  textBlack:      '#000000',

  // ── Borders ───────────────────────────────────────────────────
  borderGold:  0xffd700,
  borderDark:  0x333333,
  borderLight: 0x555555,

  // ── Domino Tiles ──────────────────────────────────────────────
  // Tile faces are light/ivory — the opposite of the dark app theme.
  tileFill:            0xf0ece4,  // Default face — warm ivory
  tileFillSelected:    0xfff8dc,  // Selected face — cornsilk gold tint
  tileBorder:          0x999999,  // Default border — light gray
  tileBorderHighlight: 0x888888,  // Playable border — medium gray
  tileDivider:         0x888888,  // Center dividing line

} as const;


// ─────────────────────────────────────────────────────────────────
// CSS COLOR STRINGS
// Use for HTML/DOM elements outside Phaser (e.g. body background)
// ─────────────────────────────────────────────────────────────────

export const CSS_COLORS = {
  bgPrimary:  '#000000',
  bgSurface:  '#1f1f1f',
  gold:       '#ffd700',
  goldDark:   '#cb9719',
  goldGlow:   '#ffe898',
  textWhite:  '#ffffff',
  textGold:   '#ffd700',
} as const;


// ─────────────────────────────────────────────────────────────────
// TYPOGRAPHY
// ─────────────────────────────────────────────────────────────────

/**
 * Font family stacks.
 * NOTE: Nunito and Montserrat must be loaded via Google Fonts in index.html:
 *   <link href="https://fonts.googleapis.com/css2?family=Nunito:wght@400;500;600;700&family=Montserrat:wght@400;500;600;700&display=swap" rel="stylesheet">
 */
export const FONTS = {
  // ── Original (preserved for backward compat) ──────────────────
  title: 'Georgia, serif',         // Serif fallback (legacy)
  body:  'Arial, sans-serif',      // Sans fallback (legacy)
  mono:  'Courier New, monospace', // Monospace

  // ── Figma design system fonts ─────────────────────────────────
  display:    'Nunito, Georgia, serif',         // Titles / hero text (Figma screen 2)
  accent:     'Montserrat, Arial, sans-serif',  // Labels / pill controls (Figma screen 1)
  supporting: 'Montserrat, Arial, sans-serif',  // Secondary body text
} as const;

/** Font weight constants (use as fontStyle or pass to CSS) */
export const FONT_WEIGHTS = {
  regular:  '400',
  medium:   '500',
  semiBold: '600',
  bold:     '700',
} as const;

export const FONT_SIZES = {
  // ── Titles & Headers ──────────────────────────────────────────
  hero:    '48px',   // Main logo / title
  display: '35px',   // Figma screen 2 title (Nunito 700 35px)
  h1:      '36px',   // Section headers
  h2:      '28px',   // Sub-headers
  h3:      '22px',   // Smaller headers

  // ── Body ──────────────────────────────────────────────────────
  large:      '20px',   // Large body / Figma accent (Montserrat 600 20px)
  medium:     '18px',   // Medium body text
  supporting: '17px',   // Figma secondary text (Montserrat 500 17px)
  body:       '16px',   // Default body text
  small:      '14px',   // Small / secondary text
  xs:         '12px',   // Tiny labels
  xxs:        '10px',   // Micro labels (seat tags, compact HUD text)

  // ── Buttons ───────────────────────────────────────────────────
  btnLarge:  '20px',   // Large CTA
  btnMedium: '16px',   // Default button (Figma CTA: Nunito 700 16px)
  btnSmall:  '14px',   // Small button
} as const;


// ─────────────────────────────────────────────────────────────────
// SPACING
// ─────────────────────────────────────────────────────────────────

export const SPACING = {
  xs:   4,
  sm:   8,
  md:   16,
  lg:   24,
  xl:   32,
  xxl:  48,
  xxxl: 64,
} as const;


// ─────────────────────────────────────────────────────────────────
// BORDER RADII
// ─────────────────────────────────────────────────────────────────

export const RADIUS = {
  // ── Original (preserved for backward compat) ──────────────────
  sm:   6,
  md:   10,
  lg:   16,
  xl:   20,
  pill: 24,   // Original pill

  // ── New ───────────────────────────────────────────────────────
  pillLg:  32,   // Figma screen 2 pill buttons (32px radius)
  circle:  9999, // Full circle (use with square element)
  card:    23,   // Session card radius (from Figma)
} as const;


// ─────────────────────────────────────────────────────────────────
// STROKE / BORDER WIDTHS
// ─────────────────────────────────────────────────────────────────

export const STROKE_WIDTHS = {
  none:   0,
  hairline: 1,
  thin:   1,
  medium: 2,
  thick:  3,
  heavy:  4,

  // Semantic aliases
  card:   3,   // Session card gold border
  focus:  4,   // Hover / focus state
  divider: 1,  // Thin decorative dividers
} as const;


// ─────────────────────────────────────────────────────────────────
// GRADIENTS
// Color pairs for Phaser linear gradient fills or CSS gradients.
// Phaser doesn't support gradient fills natively on Graphics, but
// these tokens are used in shaders, fillGradientColor on Text, or
// future canvas drawing helpers.
// ─────────────────────────────────────────────────────────────────

export const GRADIENTS = {
  gold: {
    startHex: 0xffeecc,   // Light warm gold (Figma)
    endHex:   0xad9515,   // Deep gold (Figma)
    startCSS: '#FFEECC',
    endCSS:   '#AD9515',
  },
  goldText: {
    // For Phaser Text fillGradientColor
    startHex: 0xffd700,
    endHex:   0xcb9719,
    startCSS: '#FFD700',
    endCSS:   '#CB9719',
  },
  darkSurface: {
    startHex: 0x1f1f1f,
    endHex:   0x000000,
    startCSS: '#1F1F1F',
    endCSS:   '#000000',
  },
} as const;


// ─────────────────────────────────────────────────────────────────
// GLOW / EFFECT TOKENS
// Used for glow rings, flare highlights, and luminous accents.
// In Phaser, apply via postFX pipeline or as soft circle overlays.
// ─────────────────────────────────────────────────────────────────

export const GLOW = {
  gold: {
    color:    0xffe898,   // Figma gold glow
    colorCSS: '#FFE898',
    alpha:    0.55,       // Overlay alpha for glow ring
    radius:   20,         // Glow spread (px beyond element edge)
  },
  white: {
    color:    0xffffff,   // White flare highlight
    colorCSS: '#FFFFFF',
    alpha:    0.15,
    radius:   12,
  },
  teamA: {
    color:    0x0099ff,
    colorCSS: '#0099FF',
    alpha:    0.4,
    radius:   16,
  },
  teamB: {
    color:    0xff4949,
    colorCSS: '#FF4949',
    alpha:    0.4,
    radius:   16,
  },
} as const;


// ─────────────────────────────────────────────────────────────────
// SHADOWS
// Descriptive tokens. Apply via Phaser Text shadow config or
// custom canvas drawing. Phaser Graphics does not support shadows
// natively — use these values in text style `shadow` objects.
// ─────────────────────────────────────────────────────────────────

export const SHADOWS = {
  none: {
    offsetX: 0, offsetY: 0, blur: 0,
    color: '#000000', alpha: 0, fill: false,
  },
  soft: {
    offsetX: 0, offsetY: 2, blur: 6,
    color: '#000000', alpha: 0.4, fill: true,
  },
  hard: {
    // Figma: "subtle hard black drop shadow"
    offsetX: 2, offsetY: 2, blur: 0,
    color: '#000000', alpha: 0.85, fill: true,
  },
  goldGlow: {
    offsetX: 0, offsetY: 0, blur: 10,
    color: '#FFE898', alpha: 0.6, fill: true,
  },
} as const;


// ─────────────────────────────────────────────────────────────────
// LAYOUT TOKENS
// Screen-level layout constants shared across all scenes.
// ─────────────────────────────────────────────────────────────────

export const LAYOUT = {
  headerHeight:  110,  // Logo + nav bar area
  footerHeight:   60,  // Bottom action area
  pagePaddingX:   16,  // Horizontal page margin
  pagePaddingY:   20,  // Vertical page margin
  cardMaxWidth:  360,  // Maximum card width on wide screens
  cardSpacing:   130,  // Vertical gap between session cards
  dividerAlpha:  0.3,  // Opacity of decorative divider lines
} as const;


// ─────────────────────────────────────────────────────────────────
// SURFACE STYLE PRESETS
// Ready-made surface configs for drawCard() and custom surfaces.
// ─────────────────────────────────────────────────────────────────

export interface SurfaceStyle {
  bgColor:     number;
  bgAlpha:     number;
  borderColor: number;
  borderWidth: number;
  radius:      number;
}

export const SURFACE_STYLES: Record<string, SurfaceStyle> = {
  /** Standard dark card with gold border */
  card: {
    bgColor:     COLORS.bgCard,
    bgAlpha:     0.9,
    borderColor: COLORS.borderGold,
    borderWidth: STROKE_WIDTHS.thin,
    radius:      RADIUS.lg,
  },
  /** Slightly lighter surface for nested elements */
  surface: {
    bgColor:     COLORS.bgSurface,
    bgAlpha:     1,
    borderColor: COLORS.borderDark,
    borderWidth: STROKE_WIDTHS.thin,
    radius:      RADIUS.lg,
  },
  /** Session card in the Sala lobby (matches Figma exactly) */
  sessionCard: {
    bgColor:     COLORS.bgPrimary,
    bgAlpha:     1,
    borderColor: COLORS.gold,
    borderWidth: STROKE_WIDTHS.card,
    radius:      RADIUS.card,
  },
  /** Dark pill control (Figma screen 1 — tab/toggle style) */
  pill: {
    bgColor:     COLORS.bgSurface,
    bgAlpha:     1,
    borderColor: COLORS.borderDark,
    borderWidth: STROKE_WIDTHS.thin,
    radius:      RADIUS.pillLg,
  },
  /** Transparent overlay panel */
  overlay: {
    bgColor:     COLORS.bgOverlay,
    bgAlpha:     0.85,
    borderColor: COLORS.borderDark,
    borderWidth: STROKE_WIDTHS.thin,
    radius:      RADIUS.lg,
  },
} as const;


// ─────────────────────────────────────────────────────────────────
// TEXT STYLE PRESETS
// Ready-made Phaser TextStyle objects for common text roles.
// ─────────────────────────────────────────────────────────────────

export const TEXT_STYLES = {

  // ── Original styles (preserved exactly) ──────────────────────
  title: {
    fontSize:   FONT_SIZES.hero,
    color:      COLORS.textWhite,
    fontStyle:  'bold',
    fontFamily: FONTS.title,
  } as Phaser.Types.GameObjects.Text.TextStyle,

  titleGold: {
    fontSize:   FONT_SIZES.hero,
    color:      COLORS.textGold,
    fontStyle:  'bold',
    fontFamily: FONTS.title,
  } as Phaser.Types.GameObjects.Text.TextStyle,

  h1: {
    fontSize:   FONT_SIZES.h1,
    color:      COLORS.textWhite,
    fontStyle:  'bold',
    fontFamily: FONTS.title,
  } as Phaser.Types.GameObjects.Text.TextStyle,

  h2: {
    fontSize:   FONT_SIZES.h2,
    color:      COLORS.textGold,
    fontStyle:  'bold',
    fontFamily: FONTS.title,
  } as Phaser.Types.GameObjects.Text.TextStyle,

  h3: {
    fontSize:   FONT_SIZES.h3,
    color:      COLORS.textWhite,
    fontStyle:  'bold',
    fontFamily: FONTS.body,
  } as Phaser.Types.GameObjects.Text.TextStyle,

  body: {
    fontSize:   FONT_SIZES.body,
    color:      COLORS.textWhite,
    fontFamily: FONTS.body,
  } as Phaser.Types.GameObjects.Text.TextStyle,

  bodyGold: {
    fontSize:   FONT_SIZES.body,
    color:      COLORS.textGold,
    fontFamily: FONTS.body,
  } as Phaser.Types.GameObjects.Text.TextStyle,

  small: {
    fontSize:   FONT_SIZES.small,
    color:      COLORS.textGray,
    fontFamily: FONTS.body,
  } as Phaser.Types.GameObjects.Text.TextStyle,

  label: {
    fontSize:   FONT_SIZES.xs,
    color:      COLORS.textGrayLight,
    fontFamily: FONTS.body,
  } as Phaser.Types.GameObjects.Text.TextStyle,

  // ── New styles (Figma design system) ──────────────────────────

  /** Large screen title — Nunito Bold 35px white */
  displayWhite: {
    fontSize:   FONT_SIZES.display,
    color:      COLORS.textWhite,
    fontStyle:  'bold',
    fontFamily: FONTS.display,
  } as Phaser.Types.GameObjects.Text.TextStyle,

  /** Large screen title — Nunito Bold 35px gold */
  displayGold: {
    fontSize:   FONT_SIZES.display,
    color:      COLORS.textGold,
    fontStyle:  'bold',
    fontFamily: FONTS.display,
  } as Phaser.Types.GameObjects.Text.TextStyle,

  /** CTA / button label — Nunito Bold 16px */
  cta: {
    fontSize:   FONT_SIZES.btnMedium,
    color:      COLORS.textBlack,
    fontStyle:  'bold',
    fontFamily: FONTS.display,
  } as Phaser.Types.GameObjects.Text.TextStyle,

  /** Supporting secondary text — Montserrat Medium 17px gray */
  supporting: {
    fontSize:   FONT_SIZES.supporting,
    color:      COLORS.textGrayLight,
    fontFamily: FONTS.accent,
  } as Phaser.Types.GameObjects.Text.TextStyle,

  /** Pill/tab label — Montserrat SemiBold 20px */
  pillLabel: {
    fontSize:   FONT_SIZES.large,
    color:      COLORS.textWhite,
    fontStyle:  'bold',
    fontFamily: FONTS.accent,
  } as Phaser.Types.GameObjects.Text.TextStyle,

  /** Pill/tab label — gold variant */
  pillLabelGold: {
    fontSize:   FONT_SIZES.large,
    color:      COLORS.textGold,
    fontStyle:  'bold',
    fontFamily: FONTS.accent,
  } as Phaser.Types.GameObjects.Text.TextStyle,

  /** Hard drop shadow white label (Figma screen 1 pattern) */
  shadowLabel: {
    fontSize:   FONT_SIZES.large,
    color:      COLORS.textWhite,
    fontStyle:  'bold',
    fontFamily: FONTS.accent,
    shadow: SHADOWS.hard,
  } as Phaser.Types.GameObjects.Text.TextStyle,

} as const;


// ─────────────────────────────────────────────────────────────────
// BUTTON STYLE PRESETS
// ─────────────────────────────────────────────────────────────────

export interface ButtonStyle {
  bgColor:    number;
  bgAlpha:    number;
  hoverColor: number;
  hoverAlpha: number;
  textColor:  string;
  borderColor: number;
  borderWidth: number;
  radius:     number;
  fontSize:   string;
  width:      number;
  height:     number;
}

export const BUTTON_STYLES: Record<string, ButtonStyle> = {

  // ── Original variants (preserved exactly) ────────────────────

  primary: {
    bgColor:     COLORS.btnPrimary,
    bgAlpha:     1,
    hoverColor:  0xfffacd,            // Cream gold on hover
    hoverAlpha:  1,
    textColor:   '#000000',
    borderColor: COLORS.goldBright,
    borderWidth: STROKE_WIDTHS.none,
    radius:      RADIUS.md,
    fontSize:    FONT_SIZES.btnMedium,
    width:       280,
    height:      50,
  },

  secondary: {
    bgColor:     COLORS.btnSecondary,
    bgAlpha:     0.9,
    hoverColor:  COLORS.bgCardHover,
    hoverAlpha:  1,
    textColor:   COLORS.textWhite,
    borderColor: COLORS.borderDark,
    borderWidth: STROKE_WIDTHS.medium,
    radius:      RADIUS.md,
    fontSize:    FONT_SIZES.btnMedium,
    width:       280,
    height:      50,
  },

  outline: {
    bgColor:     0x000000,
    bgAlpha:     0.3,
    hoverColor:  COLORS.bgCard,
    hoverAlpha:  0.8,
    textColor:   COLORS.textWhite,
    borderColor: COLORS.whiteFlare,
    borderWidth: STROKE_WIDTHS.medium,
    radius:      RADIUS.md,
    fontSize:    FONT_SIZES.btnMedium,
    width:       280,
    height:      50,
  },

  danger: {
    bgColor:     COLORS.btnDanger,
    bgAlpha:     0.8,
    hoverColor:  0xaa0000,
    hoverAlpha:  1,
    textColor:   COLORS.textWhite,
    borderColor: COLORS.btnDanger,
    borderWidth: STROKE_WIDTHS.none,
    radius:      RADIUS.sm,
    fontSize:    FONT_SIZES.btnSmall,
    width:       180,
    height:      40,
  },

  small: {
    bgColor:     COLORS.bgCard,
    bgAlpha:     0.9,
    hoverColor:  COLORS.bgCardHover,
    hoverAlpha:  1,
    textColor:   COLORS.textGrayLight,
    borderColor: COLORS.borderDark,
    borderWidth: STROKE_WIDTHS.thin,
    radius:      RADIUS.sm,
    fontSize:    FONT_SIZES.btnSmall,
    width:       160,
    height:      38,
  },

  // ── New variants (Figma design system) ────────────────────────

  /** Figma screen 2 — large gold pill CTA (Nunito Bold, 32px radius) */
  primaryPill: {
    bgColor:     COLORS.goldDark,     // #CB9719 — Figma primary CTA color
    bgAlpha:     1,
    hoverColor:  COLORS.gold,         // Brighter on hover
    hoverAlpha:  1,
    textColor:   '#000000',
    borderColor: COLORS.gold,
    borderWidth: STROKE_WIDTHS.none,
    radius:      RADIUS.pillLg,       // 32px — Figma pill
    fontSize:    FONT_SIZES.btnMedium,
    width:       300,
    height:      56,
  },

  /** Figma screen 2 — light secondary pill (white/light bg) */
  secondaryPill: {
    bgColor:     COLORS.btnSecondaryLight,  // #F5F5F5
    bgAlpha:     1,
    hoverColor:  0xdddddd,
    hoverAlpha:  1,
    textColor:   '#000000',
    borderColor: COLORS.borderDark,
    borderWidth: STROKE_WIDTHS.none,
    radius:      RADIUS.pillLg,
    fontSize:    FONT_SIZES.btnMedium,
    width:       300,
    height:      56,
  },

  /** Dark pill tab/toggle control (Figma screen 1 — Montserrat 600) */
  pillTab: {
    bgColor:     COLORS.bgSurface,    // #1F1F1F
    bgAlpha:     1,
    hoverColor:  COLORS.bgCard,
    hoverAlpha:  1,
    textColor:   COLORS.textGrayLight,
    borderColor: COLORS.borderDark,
    borderWidth: STROKE_WIDTHS.thin,
    radius:      RADIUS.pillLg,
    fontSize:    FONT_SIZES.large,    // 20px Montserrat
    width:       160,
    height:      44,
  },

  /** Active pill tab — gold highlighted variant */
  pillTabActive: {
    bgColor:     COLORS.goldDark,
    bgAlpha:     1,
    hoverColor:  COLORS.gold,
    hoverAlpha:  1,
    textColor:   '#000000',
    borderColor: COLORS.gold,
    borderWidth: STROKE_WIDTHS.none,
    radius:      RADIUS.pillLg,
    fontSize:    FONT_SIZES.large,
    width:       160,
    height:      44,
  },

  /** Ghost / text-only button — no background */
  ghost: {
    bgColor:     0x000000,
    bgAlpha:     0,
    hoverColor:  COLORS.bgCard,
    hoverAlpha:  0.5,
    textColor:   COLORS.textGray,
    borderColor: COLORS.borderDark,
    borderWidth: STROKE_WIDTHS.none,
    radius:      RADIUS.md,
    fontSize:    FONT_SIZES.btnSmall,
    width:       160,
    height:      38,
  },

  /**
   * dangerOutline — premium destructive action button.
   *
   * Use for "Leave Table", "Cancel", "Exit Game" — any action that navigates
   * away or ends a session. Pill shape matches the lobby's visual language.
   * Transparent background keeps it from dominating the screen, while the
   * red outline + text clearly signals a leave/cancel action.
   *
   * Idle:  dark semi-transparent bg · red outline border · soft red text
   * Hover: muted dark red fill — communicates "I mean it" on intent
   */
  dangerOutline: {
    bgColor:     0x000000,
    bgAlpha:     0.25,            // nearly transparent — sits quietly in the footer
    hoverColor:  0x5a0000,        // deep restrained red on hover
    hoverAlpha:  0.80,
    textColor:   COLORS.textRed,  // '#ff6666' — soft red, not harsh
    borderColor: 0xcc2222,        // medium red border — clear but not aggressive
    borderWidth: STROKE_WIDTHS.medium,
    radius:      RADIUS.pill,     // 24px — pill shape matches lobby aesthetic
    fontSize:    FONT_SIZES.btnSmall,
    width:       190,
    height:      44,
  },

} as const;


// ─────────────────────────────────────────────────────────────────
// HELPER: Create a styled interactive button
// ─────────────────────────────────────────────────────────────────

export function createStyledButton(
  scene:         Phaser.Scene,
  x:             number,
  y:             number,
  label:         string,
  style:         ButtonStyle,
  onClick:       () => void,
  widthOverride?: number
): Phaser.GameObjects.Container {
  const container = scene.add.container(x, y);
  const scale  = getUIScale(scene);
  const btnW   = Math.round((widthOverride ?? style.width) * scale);
  const btnH   = Math.round(style.height * scale);

  // Normal background
  const bg = scene.add.graphics();
  bg.fillStyle(style.bgColor, style.bgAlpha);
  bg.fillRoundedRect(-btnW / 2, -btnH / 2, btnW, btnH, style.radius);
  if (style.borderWidth > 0) {
    bg.lineStyle(style.borderWidth, style.borderColor, 1);
    bg.strokeRoundedRect(-btnW / 2, -btnH / 2, btnW, btnH, style.radius);
  }

  // Hover background (hidden by default)
  const hoverBg = scene.add.graphics();
  hoverBg.fillStyle(style.hoverColor, style.hoverAlpha);
  hoverBg.fillRoundedRect(-btnW / 2, -btnH / 2, btnW, btnH, style.radius);
  if (style.borderWidth > 0) {
    hoverBg.lineStyle(style.borderWidth, style.borderColor, 1);
    hoverBg.strokeRoundedRect(-btnW / 2, -btnH / 2, btnW, btnH, style.radius);
  }
  hoverBg.setAlpha(0);

  const fontPx = parseInt(style.fontSize) || 16;
  const text = scene.add.text(0, 0, label, {
    fontSize:   `${Math.round(fontPx * scale)}px`,
    color:      style.textColor,
    fontStyle:  'bold',
    fontFamily: FONTS.body,
  }).setOrigin(0.5);

  container.add([bg, hoverBg, text]);
  container.setSize(btnW, btnH);
  container.setInteractive(
    new Phaser.Geom.Rectangle(-btnW / 2, -btnH / 2, btnW, btnH),
    Phaser.Geom.Rectangle.Contains
  );

  container.on('pointerover', () => hoverBg.setAlpha(1));
  container.on('pointerout',  () => hoverBg.setAlpha(0));
  container.on('pointerdown', onClick);

  return container;
}


// ─────────────────────────────────────────────────────────────────
// HELPER: Draw a card / surface background
// ─────────────────────────────────────────────────────────────────

export function drawCard(
  scene:   Phaser.Scene,
  x:       number,
  y:       number,
  width:   number,
  height:  number,
  options?: {
    bgColor?:    number;
    bgAlpha?:    number;
    borderColor?: number;
    borderWidth?: number;
    radius?:     number;
  }
): Phaser.GameObjects.Graphics {
  const opts = {
    bgColor:     COLORS.bgCard,
    bgAlpha:     0.9,
    borderColor: COLORS.borderGold,
    borderWidth: STROKE_WIDTHS.medium,
    radius:      RADIUS.lg,
    ...options,
  };

  const scale = getUIScale(scene);
  const sw    = Math.round(width  * scale);
  const sh    = Math.round(height * scale);
  const gfx   = scene.add.graphics();
  gfx.fillStyle(opts.bgColor, opts.bgAlpha);
  gfx.fillRoundedRect(x - sw / 2, y - sh / 2, sw, sh, opts.radius);
  gfx.lineStyle(opts.borderWidth, opts.borderColor, 1);
  gfx.strokeRoundedRect(x - sw / 2, y - sh / 2, sw, sh, opts.radius);

  return gfx;
}
